// Conversão entre o formato salvo do roteiro e o grafo do React Flow.
//
// Salvo:  { startNodeId, nodes: [{ id, type, label, next, branches, position, ...config }] }
// Grafo:  nodes [{ id, type: "scriptNode", position, data }] + edges [{ source, target, sourceHandle }]
//
// A posição fica no próprio nó salvo, então o desenho que a pessoa montou volta
// igual na próxima abertura — sem isso o editor reorganizaria tudo a cada
// carregamento e a leitura visual se perderia.

const COLUMN_X = 260;
const ROW_Y = 150;

/** Distribui em coluna os nós sem posição (roteiros antigos ou importados). */
export function withPositions(nodes = []) {
  return nodes.map((node, index) => ({
    ...node,
    position: node.position || {
      x: 80 + (index % 3) * COLUMN_X,
      y: 60 + Math.floor(index / 3) * ROW_Y,
    },
  }));
}

export const BRANCH_HANDLE = (index) => `branch-${index}`;
export const NEXT_HANDLE = "next";

export function toGraph(flow, catalog) {
  const nodes = withPositions(flow?.nodes || []);

  const rfNodes = nodes.map((node) => ({
    id: node.id,
    type: "scriptNode",
    position: node.position,
    data: {
      node,
      spec: catalog.nodes[node.type],
      isStart: flow?.startNodeId === node.id,
    },
  }));

  const edges = [];

  for (const node of nodes) {
    const spec = catalog.nodes[node.type];

    (node.branches || []).forEach((branch, index) => {
      if (!branch.next) return;
      edges.push({
        id: `${node.id}-${BRANCH_HANDLE(index)}`,
        source: node.id,
        sourceHandle: BRANCH_HANDLE(index),
        target: branch.next,
        label: `${branch.when || "contém"} ${branch.value || ""}`.trim(),
        animated: true,
      });
    });

    if (node.next && !spec?.terminal) {
      edges.push({
        id: `${node.id}-${NEXT_HANDLE}`,
        source: node.id,
        sourceHandle: NEXT_HANDLE,
        target: node.next,
        label: spec?.branching ? "senão" : undefined,
      });
    }
  }

  return { nodes: rfNodes, edges };
}

/** Aplica no formato salvo o que foi movido no canvas. */
export function applyPositions(flow, rfNodes) {
  const positions = new Map(rfNodes.map((node) => [node.id, node.position]));

  return {
    ...flow,
    nodes: (flow.nodes || []).map((node) => ({
      ...node,
      position: positions.get(node.id) || node.position,
    })),
  };
}

/** Liga dois nós, respeitando de qual saída a conexão partiu. */
export function connect(flow, { source, sourceHandle, target }) {
  return {
    ...flow,
    nodes: flow.nodes.map((node) => {
      if (node.id !== source) return node;

      if (sourceHandle?.startsWith("branch-")) {
        const index = Number(sourceHandle.split("-")[1]);
        const branches = [...(node.branches || [])];
        if (!branches[index]) return node;
        branches[index] = { ...branches[index], next: target };
        return { ...node, branches };
      }

      return { ...node, next: target };
    }),
  };
}

/** Desfaz uma ligação a partir da aresta removida. */
export function disconnect(flow, edge) {
  return {
    ...flow,
    nodes: flow.nodes.map((node) => {
      if (node.id !== edge.source) return node;

      if (edge.sourceHandle?.startsWith("branch-")) {
        const index = Number(edge.sourceHandle.split("-")[1]);
        const branches = [...(node.branches || [])];
        if (!branches[index]) return node;
        branches[index] = { ...branches[index], next: null };
        return { ...node, branches };
      }

      return { ...node, next: null };
    }),
  };
}

/** Remove o nó e qualquer ligação que apontava para ele. */
export function removeNode(flow, id) {
  const nodes = flow.nodes
    .filter((node) => node.id !== id)
    .map((node) => ({
      ...node,
      next: node.next === id ? null : node.next,
      branches: (node.branches || []).map((branch) =>
        branch.next === id ? { ...branch, next: null } : branch,
      ),
    }));

  return {
    startNodeId: flow.startNodeId === id ? nodes[0]?.id || null : flow.startNodeId,
    nodes,
  };
}
